import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Route, Plus, Pencil, Trash2, ChevronDown, ChevronRight, MapPin, CalendarDays, Users, X, Anchor, Eye, EyeOff, GripVertical, Waves, Compass,
} from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import PortThumbnail from "@/components/PortThumbnail";

// Small helper to fetch multiple candidate photos for a port so the
// operator can pick a nicer one than the auto-resolver default.
// State lives inside `PhotoPickerBlock` — kept out of the parent so the
// itinerary form doesn't re-render every keystroke of the port input.
function PhotoPickerBlock({ portName, value, onPick }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [candidates, setCandidates] = React.useState([]);
  const fetchCandidates = async () => {
    if (!portName?.trim()) { toast.error("Type a location first"); return; }
    setLoading(true);
    setOpen(true);
    try {
      const { data } = await api.get(`/public/port-photos?name=${encodeURIComponent(portName.trim())}&limit=6`);
      setCandidates(data?.candidates || []);
    } catch (e) {
      toast.error("Couldn't fetch photos — try again in a moment");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="border border-border/60 bg-muted/20 p-3 rounded" data-testid="itinerary-form-photo-picker">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] text-muted-foreground">
          {value ? "Using a manually-picked photo." : "Using the auto-fetched reference photo."}
        </div>
        <div className="flex items-center gap-2">
          {value && (
            <Button type="button" size="sm" variant="outline" onClick={() => onPick(null)} data-testid="itinerary-form-photo-clear" className="text-xs h-8">
              Reset
            </Button>
          )}
          <Button type="button" size="sm" variant="outline" onClick={fetchCandidates} disabled={loading || !portName?.trim()} data-testid="itinerary-form-photo-browse" className="text-xs h-8">
            {loading ? "Searching…" : "Browse photos"}
          </Button>
        </div>
      </div>
      {open && (
        <div className="mt-3">
          {loading ? (
            <div className="text-xs text-muted-foreground py-6 text-center">Fetching candidates from Wikipedia…</div>
          ) : candidates.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4 text-center italic">No alternative photos found — the auto-fetched one will be used.</div>
          ) : (
            <div className="grid grid-cols-3 gap-2 mt-2">
              {candidates.map((c, i) => {
                const active = value === c.url;
                return (
                  <button
                    key={c.url}
                    type="button"
                    onClick={() => { onPick(c.url); toast.success("Photo picked — save the stop to keep."); }}
                    className={`relative overflow-hidden rounded border-2 transition-all ${active ? "border-accent ring-2 ring-accent/40" : "border-border/60 hover:border-accent/60"}`}
                    style={{ aspectRatio: "1" }}
                    data-testid={`itinerary-form-photo-candidate-${i}`}
                    title={c.title || "candidate photo"}
                  >
                    <img src={c.url} alt={c.title || "candidate"} className="w-full h-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                    {active && (
                      <div className="absolute inset-0 bg-accent/20 flex items-end justify-center pb-1">
                        <span className="text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 uppercase tracking-widest">Picked</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Smart yachting labels for the three stop types — used in badges,
// dialogs, and the guest timeline so the vocabulary stays consistent.
const STOP_TYPES = {
  port:    { label: "On Port",   hint: "Docked in harbour",       icon: MapPin,  tone: "border-primary/30 text-primary/80" },
  anchor:  { label: "At Anchor", hint: "Moored offshore, tender", icon: Anchor,  tone: "border-accent/40 text-accent" },
  passage: { label: "At Sea",    hint: "Sea day, on passage",     icon: Waves,   tone: "border-sky-500/40 text-sky-700 dark:text-sky-400" },
};
const stopMeta = (t) => STOP_TYPES[(t || "port")] || STOP_TYPES.port;

const emptyForm = { port: "", country: "", arrive_at: "", depart_at: "", notes: "", stop_type: "port", lat: null, lon: null, image_url: null };

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}
// Convert an ISO datetime to the `datetime-local` input format
// (YYYY-MM-DDTHH:MM in the browser's local zone) so the operator sees
// the same wall-clock time the yacht keeps. `slice(0,16)` was chosen
// over `.toISOString()` because ISO output is UTC-anchored and would
// scroll the picker into the wrong hour.
function toDateTimeInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  } catch { return ""; }
}
function toIso(str) {
  if (!str) return "";
  // datetime-local strings ("YYYY-MM-DDTHH:MM") are naive — a bare `new
  // Date()` treats them as local wall-clock time, which is what the
  // manager typed. Send the resulting UTC-anchored ISO to the server.
  if (str.length >= 16) {
    try { return new Date(str).toISOString(); } catch { return str; }
  }
  // Legacy date-only rows — no ETA/ETD info, default to midnight UTC.
  if (str.length === 10) return `${str}T00:00:00+00:00`;
  return str;
}

const gIdent = (g) => g?.id || g?.guest_id;

export default function ItinerarySection({ charter }) {
  const { admin } = useAuth();
  const canEdit = ["developer", "admin", "manager"].includes(admin?.role);
  const cid = charter.id;

  const [open, setOpen] = useState(false);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // stop being edited, or null for new
  const [form, setForm] = useState(emptyForm);
  const [expandedStopId, setExpandedStopId] = useState(null);
  const [visible, setVisible] = useState(false);
  const [visibilityBusy, setVisibilityBusy] = useState(false);
  // Geocode disambiguation — when a name+country yields more than one
  // OSM hit, we surface a dropdown so the manager picks the exact place.
  const [candidates, setCandidates] = useState(null); // null | array of results
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  // Drag-drop state for same-day reordering. `dragId` = stop being dragged;
  // `dragOverId` = the row currently under the cursor (used for the ring
  // highlight). Cleared on drop / dragEnd / dragLeave.
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  // Re-pin every stop's coordinates against OSM. Guarded by a busy flag so
  // repeated clicks don't spawn parallel Overpass calls (which the API
  // rate-limits aggressively). Missing this state used to ReferenceError on
  // every render once the itinerary was opened, crashing TripsPanel back to
  // the default Booking tab.
  const [repinBusy, setRepinBusy] = useState(false);

  const guests = useMemo(() => (charter.guests || []).filter((g) => gIdent(g)), [charter.guests]);

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const [stopsRes, visRes] = await Promise.all([
        api.get(`/admin/charters/${cid}/itinerary`),
        api.get(`/admin/charters/${cid}/itinerary-visibility`).catch(() => ({ data: { itinerary_public: false } })),
      ]);
      setStops(stopsRes.data || []);
      setVisible(!!visRes.data?.itinerary_public);
    } catch (e) {
      // Manager+ only — supervisors will get 403 and that's fine.
      if (e.response?.status !== 403) toast.error(formatErr(e.response?.data?.detail) || "Itinerary load failed");
    } finally { setLoading(false); }
  }, [cid]);

  const repinAll = async () => {
    if (repinBusy) return;
    if (!stops.length) { toast.info("No stops to re-pin yet."); return; }
    if (!window.confirm(
      "Re-pin every location on this itinerary?\n\n" +
      "We'll query OpenStreetMap for the closest marina / harbour and " +
      "update the stored coordinates. Stops already sitting on their " +
      "marina are left alone. Sea days are skipped."
    )) return;
    setRepinBusy(true);
    // Iterate stops one at a time — Overpass has strict rate limits and
    // this also lets us show live progress rather than freeze the UI
    // for the full ~5 s per stop.
    const toRefresh = stops.filter((s) => (s.stop_type || "port") !== "passage");
    const t = toast.loading(`Re-pinning 0 / ${toRefresh.length}…`);
    let updated = 0, unchanged = 0, failed = 0;
    for (let i = 0; i < toRefresh.length; i++) {
      const s = toRefresh[i];
      toast.loading(`Re-pinning ${i + 1} / ${toRefresh.length} · ${s.port}`, { id: t });
      try {
        const { data } = await api.post(`/admin/charters/${cid}/itinerary/${s.id}/refresh-coord`);
        if (data?.status === "updated") updated++;
        else if (data?.status === "unchanged") unchanged++;
        else failed++;
      } catch { failed++; }
    }
    const parts = [];
    if (updated) parts.push(`${updated} moved to marina`);
    if (unchanged) parts.push(`${unchanged} already correct`);
    if (failed) parts.push(`${failed} could not resolve`);
    toast.success(parts.join(" · ") || "No changes needed", { id: t });
    await load();
    setRepinBusy(false);
  };

  const toggleVisibility = async () => {
    if (visibilityBusy) return;
    setVisibilityBusy(true);
    const next = !visible;
    // Optimistic — flip immediately, roll back on failure.
    setVisible(next);
    try {
      await api.put(`/admin/charters/${cid}/itinerary-visibility`, { itinerary_public: next });
      toast.success(next ? "Itinerary now visible to guests" : "Itinerary hidden from guests");
    } catch (e) {
      setVisible(!next);
      toast.error(formatErr(e.response?.data?.detail) || "Could not toggle visibility");
    } finally { setVisibilityBusy(false); }
  };

  // Same-day drag & drop helpers. YYYY-MM-DD from the ISO string is enough
  // to test "same day" and matches the server-side validation.
  const dayKey = (iso) => (iso || "").slice(0, 10);
  const sameDaySiblingIds = (stop) => stops.filter((s) => dayKey(s.arrive_at) === dayKey(stop.arrive_at)).map((s) => s.id);
  const isSameDayTarget = (a, b) => a && b && a.id !== b.id && dayKey(a.arrive_at) === dayKey(b.arrive_at);

  const onDragStart = (stop) => (e) => {
    if (!canEdit) return;
    setDragId(stop.id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", stop.id); } catch { /* ignore */ }
  };
  const onDragOver = (target) => (e) => {
    if (!dragId) return;
    const src = stops.find((s) => s.id === dragId);
    if (!src || !isSameDayTarget(src, target)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(target.id);
  };
  const onDragLeave = (target) => () => {
    if (dragOverId === target.id) setDragOverId(null);
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };
  const onDrop = (target) => async (e) => {
    e.preventDefault();
    const srcId = dragId || e.dataTransfer?.getData("text/plain");
    setDragId(null);
    setDragOverId(null);
    if (!srcId || srcId === target.id) return;
    const src = stops.find((s) => s.id === srcId);
    if (!src || !isSameDayTarget(src, target)) return;
    // Build the new order of same-day siblings by moving src just before target.
    const siblings = sameDaySiblingIds(target).filter((id) => id !== srcId);
    const insertAt = siblings.indexOf(target.id);
    siblings.splice(insertAt, 0, srcId);
    // Optimistic reorder in local state.
    const other = stops.filter((s) => dayKey(s.arrive_at) !== dayKey(target.arrive_at));
    const orderedSame = siblings.map((id) => stops.find((s) => s.id === id)).filter(Boolean);
    setStops([...other, ...orderedSame].sort((a, b) => (a.arrive_at || "").localeCompare(b.arrive_at || "")));
    try {
      await api.put(`/admin/charters/${cid}/itinerary/reorder`, { ordered_ids: siblings });
      toast.success("Order updated");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Reorder failed");
      load();
    }
  };

  useEffect(() => { if (open) load(); }, [open, load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setCandidates(null); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      port: s.port || "",
      country: s.country || "",
      arrive_at: toDateTimeInput(s.arrive_at),
      depart_at: toDateTimeInput(s.depart_at),
      notes: s.notes || "",
      stop_type: ["port", "anchor", "passage"].includes(s.stop_type) ? s.stop_type : "port",
      lat: s.lat ?? null,
      lon: s.lon ?? null,
      image_url: s.image_url ?? null,
    });
    setCandidates(null);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.port.trim()) { toast.error("Location name required"); return; }
    if (!form.country.trim()) { toast.error("Country required"); return; }
    if (!form.arrive_at) { toast.error("Arrival date required"); return; }
    // Skip the geocode picker for At Sea (passage) — a sea day doesn't
    // land on a place, we let the frontend pin drift where it may.
    // If the manager already picked one from a previous candidate list
    // this save-cycle, we trust that pick and don't re-query.
    if (form.stop_type !== "passage" && form.lat == null && form.lon == null) {
      setCandidatesLoading(true);
      try {
        const { data } = await api.get("/public/geocode/candidates", {
          params: { name: form.port.trim(), country: form.country.trim(), limit: 5 },
        });
        const results = data?.results || [];
        setCandidatesLoading(false);
        if (results.length > 1) {
          // Multiple matches — surface a picker instead of silently
          // guessing. Manager picks, then hits Save again with lat/lon
          // populated on the form.
          setCandidates(results);
          toast.info("Multiple places match — pick the right one below.");
          return;
        }
        if (results.length === 1) {
          setForm((f) => ({ ...f, lat: results[0].lat, lon: results[0].lon }));
        }
      } catch (_e) {
        setCandidatesLoading(false);
        // Don't block save on a geocode outage — the map falls back to
        // name+country resolution at view time.
      }
    }
    try {
      const payload = {
        port: form.port.trim(),
        country: form.country.trim(),
        arrive_at: toIso(form.arrive_at),
        depart_at: form.depart_at ? toIso(form.depart_at) : null,
        notes: form.notes.trim() || null,
        guest_ids: editing?.guest_ids || [],
        stop_type: ["port", "anchor", "passage"].includes(form.stop_type) ? form.stop_type : "port",
        lat: form.lat,
        lon: form.lon,
        image_url: form.image_url,
      };
      let newId = null;
      if (editing) await api.put(`/admin/charters/${cid}/itinerary/${editing.id}`, payload);
      else {
        const { data } = await api.post(`/admin/charters/${cid}/itinerary`, payload);
        newId = data?.id;
      }
      toast.success(editing ? "Stop updated" : "Stop added — now tick who's joining");
      setDialogOpen(false);
      setCandidates(null);
      await load();
      if (newId) setExpandedStopId(newId);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.port} from the itinerary?`)) return;
    try {
      await api.delete(`/admin/charters/${cid}/itinerary/${s.id}`);
      toast.success("Stop removed");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Remove failed"); }
  };

  const toggleGuest = async (stop, guestId) => {
    const currentIds = new Set(stop.guest_ids || []);
    if (currentIds.has(guestId)) currentIds.delete(guestId); else currentIds.add(guestId);
    const nextIds = Array.from(currentIds);
    // Optimistic update so the checkbox feels instant.
    setStops((prev) => prev.map((s) => s.id === stop.id ? { ...s, guest_ids: nextIds } : s));
    try {
      await api.put(`/admin/charters/${cid}/itinerary/${stop.id}/guests`, { guest_ids: nextIds });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not update roster");
      load(); // rollback via reload
    }
  };

  const setAllForStop = async (stop, all) => {
    const nextIds = all ? guests.map(gIdent) : [];
    setStops((prev) => prev.map((s) => s.id === stop.id ? { ...s, guest_ids: nextIds } : s));
    try {
      await api.put(`/admin/charters/${cid}/itinerary/${stop.id}/guests`, { guest_ids: nextIds });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Update failed");
      load();
    }
  };

  return (
    <div className="border-t border-border" data-testid={`itinerary-section-${cid}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 hover:bg-muted/30"
        data-testid={`itinerary-toggle-${cid}`}
      >
        <span className="flex items-center gap-3 font-display text-lg">
          <Route className="h-4 w-4 text-accent" />
          Itinerary
          <Badge variant="outline" className="ml-1">{stops.length}</Badge>
        </span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      {open && (
        <div className="p-5 pt-0 space-y-4" data-testid={`itinerary-body-${cid}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground max-w-lg">
              Chronological stops for this charter. Tick which passengers join each port — freely editable later.
            </p>
            <div className="flex items-center gap-2">
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleVisibility}
                  disabled={visibilityBusy}
                  className={visible ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/5" : ""}
                  data-testid={`itinerary-visibility-${cid}`}
                  title={visible ? "Guests can see their itinerary on /journey" : "Guests do not see the itinerary section"}
                >
                  {visible ? <Eye className="h-3.5 w-3.5 mr-1.5" /> : <EyeOff className="h-3.5 w-3.5 mr-1.5" />}
                  {visible ? "Visible to guests" : "Hidden from guests"}
                </Button>
              )}
              {canEdit && stops.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={repinAll}
                  disabled={repinBusy}
                  className="border-accent/40 text-accent hover:bg-accent/5"
                  data-testid={`itinerary-repin-${cid}`}
                  title="Refresh every pin to the closest real marina / harbour on OpenStreetMap"
                >
                  <Compass className={`h-3.5 w-3.5 mr-1.5 ${repinBusy ? "animate-spin" : ""}`} />
                  {repinBusy ? "Re-pinning…" : "Re-pin locations"}
                </Button>
              )}
              {canEdit && (
                <Button size="sm" onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`itinerary-new-${cid}`}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add stop
                </Button>
              )}
            </div>
          </div>

          {loading && <div className="text-xs text-muted-foreground italic">Loading itinerary…</div>}

          {!loading && stops.length === 0 && (
            <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid={`itinerary-empty-${cid}`}>
              <Anchor className="h-5 w-5 mx-auto mb-2 text-accent/60" strokeWidth={1.4} />
              No stops yet — {canEdit ? "add the first port to begin." : "the manager will add ports soon."}
            </div>
          )}

          {stops.map((s) => {
            const expanded = expandedStopId === s.id;
            const checkedCount = (s.guest_ids || []).length;
            const totalGuests = guests.length;
            const emptyRoster = totalGuests > 0 && checkedCount === 0;
            // A stop is draggable only when it shares an arrival day with
            // another stop — solo-day ports have nothing to swap with.
            const sameDayCount = stops.filter((x) => dayKey(x.arrive_at) === dayKey(s.arrive_at)).length;
            const canDrag = canEdit && sameDayCount > 1;
            const isDragging = dragId === s.id;
            const isDropTarget = dragOverId === s.id;
            return (
              <div
                key={s.id}
                draggable={canDrag}
                onDragStart={canDrag ? onDragStart(s) : undefined}
                onDragOver={onDragOver(s)}
                onDragLeave={onDragLeave(s)}
                onDrop={onDrop(s)}
                onDragEnd={onDragEnd}
                className={`border bg-card transition-all ${
                  isDropTarget ? "border-accent ring-2 ring-accent/40" :
                  isDragging ? "border-primary/50 opacity-60" :
                  emptyRoster ? "border-amber-500/40" :
                  "border-border"
                }`}
                data-testid={`itinerary-stop-${s.id}`}
              >
                <div className="flex items-center gap-3 p-4">
                  {canDrag && (
                    <GripVertical
                      className="h-4 w-4 text-primary/40 cursor-grab active:cursor-grabbing shrink-0"
                      data-testid={`itinerary-drag-handle-${s.id}`}
                      aria-label="Drag to reorder within the same day"
                    />
                  )}
                  <PortThumbnail name={s.port} size={44} testId={`itinerary-stop-thumb-${s.id}`} imageOverride={s.image_url} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-display text-lg tracking-tight">{s.port}</span>
                      {s.country && <span className="text-xs text-muted-foreground">· {s.country}</span>}
                      <Badge
                        variant="outline"
                        className={`text-[10px] ml-1 inline-flex items-center gap-1 ${stopMeta(s.stop_type).tone}`}
                        data-testid={`itinerary-stop-type-${s.id}`}
                      >
                        {(() => { const Icon = stopMeta(s.stop_type).icon; return <><Icon className="h-2.5 w-2.5" /> {stopMeta(s.stop_type).label}</>; })()}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{fmtDate(s.arrive_at)}{s.depart_at ? ` → ${fmtDate(s.depart_at)}` : ""}</span>
                      <span className={`inline-flex items-center gap-1 ${emptyRoster ? "text-amber-700 dark:text-amber-400 font-medium" : ""}`}>
                        <Users className="h-3 w-3" />{checkedCount}/{totalGuests} joining
                      </span>
                    </div>
                    {s.notes && <div className="text-xs text-foreground/70 mt-1 italic line-clamp-1">"{s.notes}"</div>}
                    {emptyRoster && !expanded && canEdit && (
                      <div className="text-[11px] text-amber-700 dark:text-amber-400 mt-1" data-testid={`itinerary-stop-empty-hint-${s.id}`}>
                        No guests ticked in yet — tap <span className="font-semibold">Guests</span> to opt them in.
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpandedStopId(expanded ? null : s.id)}
                    className={`text-xs uppercase tracking-widest px-2 py-1 transition-colors ${emptyRoster ? "text-amber-700 dark:text-amber-400 hover:text-amber-800" : "text-primary/60 hover:text-primary"}`}
                    data-testid={`itinerary-stop-expand-${s.id}`}
                    aria-expanded={expanded}
                  >
                    {expanded ? "Hide guests" : "Guests"}
                  </button>
                  {canEdit && (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`itinerary-edit-${s.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(s)} className="text-destructive border-destructive/40" data-testid={`itinerary-delete-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  )}
                </div>

                {expanded && (
                  <div className="border-t border-border p-4 bg-muted/20" data-testid={`itinerary-stop-guests-${s.id}`}>
                    {guests.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic">Add guests to this trip first.</div>
                    ) : (
                      <>
                        {canEdit && (
                          <div className="flex justify-end gap-2 mb-3">
                            <button
                              type="button"
                              onClick={() => setAllForStop(s, true)}
                              className="text-[11px] uppercase tracking-widest text-primary/70 hover:text-primary"
                              data-testid={`itinerary-select-all-${s.id}`}
                            >
                              Select all
                            </button>
                            <span className="text-primary/30">·</span>
                            <button
                              type="button"
                              onClick={() => setAllForStop(s, false)}
                              className="text-[11px] uppercase tracking-widest text-primary/70 hover:text-primary"
                              data-testid={`itinerary-clear-all-${s.id}`}
                            >
                              Clear
                            </button>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {(() => {
                            // Hide guests who have already disembarked BEFORE the
                            // stop starts, or who haven't embarked YET at the stop's
                            // end. `disembark_at` / `embark_at` are ISO strings so a
                            // direct lexicographic compare works when both sides are
                            // stored in the same UTC-anchored form (which the API
                            // now enforces via `toIso`). Missing dates fall back
                            // to the charter window, so those guests always appear.
                            const stopStart = s.arrive_at || "";
                            const stopEnd = s.depart_at || s.arrive_at || "";
                            const eligible = guests.filter((g) => {
                              if (g.disembark_at && stopStart && g.disembark_at < stopStart) return false;
                              if (g.embark_at && stopEnd && g.embark_at > stopEnd) return false;
                              return true;
                            });
                            if (eligible.length === 0) {
                              return (
                                <div className="col-span-full text-xs text-muted-foreground italic" data-testid={`itinerary-stop-guests-empty-${s.id}`}>
                                  No guests are aboard for this stop — everyone has disembarked or hasn't embarked yet.
                                </div>
                              );
                            }
                            return eligible.map((g) => {
                              const gid = gIdent(g);
                              const checked = (s.guest_ids || []).includes(gid);
                              return (
                                <label
                                  key={gid}
                                  className={`flex items-center gap-3 px-3 py-2 border transition-colors cursor-pointer ${checked ? "border-accent/50 bg-accent/5" : "border-border hover:border-primary/40"}`}
                                  data-testid={`itinerary-guest-row-${s.id}-${gid}`}
                                >
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => canEdit && toggleGuest(s, gid)}
                                    disabled={!canEdit}
                                    data-testid={`itinerary-guest-check-${s.id}-${gid}`}
                                  />
                                  <span className="flex-1 min-w-0">
                                    <span className="text-sm font-medium truncate block">{g.name}</span>
                                    <span className="text-[11px] text-muted-foreground truncate block">{g.cabin_name}</span>
                                  </span>
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="itinerary-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
              <MapPin className="h-5 w-5 text-accent" />
              {editing ? "Edit stop" : "New stop"}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Add a port with arrival and (optionally) departure. Guests can be ticked in from the stop's Guests panel afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline text-primary/60">Location name</Label>
              <div className="mt-2 flex items-center gap-3">
                <div className="shrink-0">
                  {form.port.trim() ? (
                    <PortThumbnail name={form.port.trim()} size={48} testId="itinerary-form-thumb" imageOverride={form.image_url} />
                  ) : (
                    <div className="rounded-full border border-dashed border-border/60 bg-muted/40 flex items-center justify-center" style={{ width: 48, height: 48 }} aria-hidden>
                      <MapPin className="h-4 w-4 text-primary/40" />
                    </div>
                  )}
                </div>
                <Input
                  value={form.port}
                  onChange={(e) => setForm({ ...form, port: e.target.value, lat: null, lon: null })}
                  placeholder="e.g. Portofino"
                  data-testid="itinerary-form-port"
                  className="flex-1"
                />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">A scenic reference photo will be fetched automatically.</p>
            </div>

            {/* Optional photo picker — lets the manager override the
                auto-fetched Wikipedia thumbnail with a nicer candidate
                from the same source. */}
            <PhotoPickerBlock
              portName={form.port}
              value={form.image_url}
              onPick={(u) => setForm((f) => ({ ...f, image_url: u }))}
            />
            <div>
              <Label className="overline text-primary/60">Country <span className="normal-case tracking-normal text-accent text-[10px]">· required</span></Label>
              <Input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value, lat: null, lon: null })}
                placeholder="e.g. Italy"
                data-testid="itinerary-form-country"
                className="mt-2"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Used to disambiguate places sharing the same name.</p>
            </div>
            {candidates && candidates.length > 1 && (
              <div className="border border-accent/40 bg-accent/5 p-3 rounded" data-testid="itinerary-form-candidates">
                <div className="overline text-accent text-[10px] mb-2">Multiple places match — pick one</div>
                <div className="space-y-1">
                  {candidates.map((c, i) => (
                    <button
                      key={`${c.lat}-${c.lon}-${i}`}
                      type="button"
                      onClick={() => {
                        setForm((f) => ({ ...f, lat: c.lat, lon: c.lon }));
                        setCandidates(null);
                        toast.success("Location pinned — tap Save to confirm.");
                      }}
                      className="w-full text-left text-xs p-2 border border-border hover:border-accent hover:bg-accent/10 transition-colors rounded"
                      data-testid={`itinerary-form-candidate-${i}`}
                    >
                      <div className="font-medium text-foreground truncate">{c.display_name}</div>
                      <div className="text-muted-foreground text-[10px] tabular-nums mt-0.5">
                        {c.lat.toFixed(3)}, {c.lon.toFixed(3)}{c.type ? ` · ${c.type}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline text-primary/60">
                  ETA <span className="normal-case tracking-normal text-muted-foreground text-[10px]">(arrive)</span>
                </Label>
                <Input type="datetime-local" value={form.arrive_at} onChange={(e) => setForm({ ...form, arrive_at: e.target.value })} data-testid="itinerary-form-arrive" className="mt-2" />
              </div>
              <div>
                <Label className="overline text-primary/60">
                  ETD <span className="normal-case tracking-normal text-muted-foreground text-[10px]">(depart, optional)</span>
                </Label>
                <Input type="datetime-local" value={form.depart_at} onChange={(e) => setForm({ ...form, depart_at: e.target.value })} data-testid="itinerary-form-depart" className="mt-2" />
              </div>
            </div>
            <div>
              <Label className="overline text-primary/60">Stop type</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Stop type">
                {["port", "anchor", "passage"].map((id) => {
                  const meta = STOP_TYPES[id];
                  const Icon = meta.icon;
                  const active = form.stop_type === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setForm({ ...form, stop_type: id })}
                      data-testid={`itinerary-form-type-${id}`}
                      className={`flex items-center gap-3 p-3 border text-left transition-colors ${active ? "border-accent bg-accent/5 text-foreground" : "border-border hover:border-primary/40 text-muted-foreground"}`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${active ? "text-accent" : "text-primary/50"}`} />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{meta.label}</span>
                        <span className="block text-[10px] leading-tight">{meta.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="overline text-primary/60">Notes <span className="normal-case tracking-normal text-muted-foreground text-[10px]">(optional)</span></Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Sunset dinner ashore, tender at 18:00"
                data-testid="itinerary-form-notes"
                className="mt-2"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setDialogOpen(false); setCandidates(null); }}>
                <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
              </Button>
              <Button onClick={save} disabled={candidatesLoading} className="bg-primary hover:bg-primary/90" data-testid="itinerary-form-save">
                {candidatesLoading ? "Locating…" : editing ? "Save" : "Add stop"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
